// Province-aware JSON-LD schema builders. All schemas pull from the active
// ProvincePack so a new province deployment "just works" without code edits.

import { COMPANY } from "./company";
import { getActiveProvince } from "@/provinces/_registry";
import { DEFAULT_OG_IMAGE } from "./seo";
import { getActiveSiteUrl } from "./seo-helpers";
import { HOME_ORDER_STEPS } from "@/content/home-steps";

export function buildOrganizationSchema() {
  const { config } = getActiveProvince();
  const site = getActiveSiteUrl();
  const primary =
    config.branches.find((b) => b.id === config.primaryBranchId) ??
    config.branches[0];

  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${site}/#organization`,
    name: `${COMPANY.brandName} — ${config.nameEn}`,
    legalName: COMPANY.legalName,
    url: site,
    telephone: COMPANY.phoneIntl,
    email: COMPANY.email,
    image: DEFAULT_OG_IMAGE,
    logo: `${site}/favicon.webp`,
    priceRange: config.priceRange,
    taxID: COMPANY.registrationNumber,
    sameAs: COMPANY.websites,
    address: primary
      ? {
          "@type": "PostalAddress",
          streetAddress: primary.address,
          addressLocality: primary.locality,
          addressRegion: primary.region,
          postalCode: primary.postalCode,
          addressCountry: "TH",
        }
      : undefined,
    geo: primary
      ? {
          "@type": "GeoCoordinates",
          latitude: primary.geo.lat,
          longitude: primary.geo.lng,
        }
      : undefined,
    hasMap: primary?.mapUrl,
    location: config.branches.map((b) => ({
      "@type": "Place",
      name: b.name,
      address: {
        "@type": "PostalAddress",
        streetAddress: b.address,
        addressLocality: b.locality,
        addressRegion: b.region,
        postalCode: b.postalCode,
        addressCountry: "TH",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: b.geo.lat,
        longitude: b.geo.lng,
      },
    })),
    areaServed: [
      { "@type": "City", name: config.nameEn },
      { "@type": "AdministrativeArea", name: `${config.nameEn} Province` },
      ...config.nearbyProvinces.map((p) => ({ "@type": "AdministrativeArea", name: p })),
    ],
    openingHours: "Mo-Su 08:00-20:00",
    knowsLanguage: ["th", "en", "ru", "zh", "de", "fr"],
    slogan: `Notary Public ${config.nameEn} — Expat · Yacht · Property · Visa`,
    foundingDate: "2017-01-01",
    numberOfEmployees: { "@type": "QuantitativeValue", value: 18 },
    award: [
      "Lawyers Council of Thailand — Registered Notarial Services Attorney",
      "Ministry of Justice — Listed Court Translator (Thai ↔ English)",
      "NAATI — Certified Translator network (AU/NZ documents)",
    ],
    hasCredential: [
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "Professional Certification",
        name: "Notarial Services Attorney (NSA)",
        recognizedBy: {
          "@type": "Organization",
          name: "Lawyers Council of Thailand",
          url: "https://www.lawyerscouncil.or.th/",
        },
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "Professional Certification",
        name: "Court-listed Translator (Thai ↔ English)",
        recognizedBy: {
          "@type": "GovernmentOrganization",
          name: "Ministry of Justice, Thailand",
          url: "https://www.moj.go.th/",
        },
      },
    ],
    memberOf: {
      "@type": "Organization",
      name: "Lawyers Council of Thailand",
      url: "https://www.lawyerscouncil.or.th/",
    },
    publishingPrinciples: `${site}/terms`,
    knowsAbout: [
      "Notary Public Thailand",
      "Apostille / MFA Consular Legalization",
      "Embassy Document Authentication",
      "NAATI Certified Translation",
      "Ministry of Justice (MoJ) Certified Translator",
      "Thai Police Clearance Certificate",
      "Thailand Work Permit & Non-B Visa",
      "DTV / LTR / Elite Visa Consultation",
      "Marriage Registration in Thailand for Foreigners",
      "APEC Business Travel Card",
      "Medical Council Certification (แพทยสภา)",
    ],
    serviceType: [
      "Notary Public",
      "Certified Translation",
      "Consular Legalization",
      "Visa & Work Permit Consultation",
      "Marriage Registration Consultation",
      "Police Clearance Certificate",
    ],
  };
}

export function buildWebSiteSchema() {
  const { config } = getActiveProvince();
  const site = getActiveSiteUrl();
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${site}/#website`,
    url: site,
    name: `${COMPANY.brandName} — ${config.nameEn}`,
    inLanguage: ["th-TH", "en"],
    publisher: { "@id": `${site}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${site}/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildBreadcrumbSchema(items: { name: string; url: string }[]) {
  const site = getActiveSiteUrl();
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((i, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: i.name,
      item: i.url.startsWith("http") ? i.url : `${site}${i.url}`,
    })),
  };
}

export function buildFaqSchema(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
  };
}

/**
 * DEPRECATED — site-wide self-serving AggregateRating was removed because
 * unverifiable, non-visible review markup is treated as spammy structured
 * data by Google. Returns `undefined` so existing
 * `aggregateRating: buildAggregateRating()` call sites simply drop the key
 * when serialized to JSON-LD. Do not reintroduce ratings unless real,
 * independently verifiable reviews are rendered on that same page.
 */
export function buildAggregateRating(): undefined {
  return undefined;
}

export function buildServiceListSchema(
  services: { name: string; description: string; url?: string; priceFrom?: number }[],
) {
  const { config } = getActiveProvince();
  const site = getActiveSiteUrl();
  return services.map((s, idx) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${site}/#service-${idx}`,
    name: s.name,
    description: s.description,
    provider: { "@id": `${site}/#organization` },
    areaServed: [
      { "@type": "City", name: config.nameEn },
      { "@type": "AdministrativeArea", name: `${config.nameEn} Province` },
    ],
    serviceType: s.name,
    url: s.url ? (s.url.startsWith("http") ? s.url : `${site}${s.url}`) : site,
    ...(s.priceFrom
      ? {
          offers: {
            "@type": "AggregateOffer",
            lowPrice: s.priceFrom,
            price: s.priceFrom,
            priceCurrency: "THB",
            offerCount: 1,
            availability: "https://schema.org/InStock",
            priceSpecification: {
              "@type": "PriceSpecification",
              price: s.priceFrom,
              priceCurrency: "THB",
              valueAddedTaxIncluded: false,
              description: "ราคาเริ่มต้น",
            },
          },
        }
      : {}),
  }));
}

export function buildHowToSchema() {
  const { config } = getActiveProvince();
  return buildHowToStepsSchema({
    name: `วิธีใช้บริการ Notary Public ใน${config.nameTh}`,
    description: `ขั้นตอนใช้บริการ Notary, แปลเอกสาร และรับรองกงสุลในจังหวัด${config.nameTh}`,
    path: "/",
    // Mirrors the visible "สั่งงานง่ายใน 4 ขั้นตอน" section on the homepage.
    steps: HOME_ORDER_STEPS.map((s, i) => ({ step: i + 1, title: s.t, body: s.d })),
  });
}

/**
 * Generic HowTo builder for any page that documents a real, ordered process.
 * Accepts either plain strings or {step,title,body} rows so every route family
 * (visa types, work-permit types, district × service, estate × service,
 * country × service, embassies) can emit a page-specific HowTo node.
 */
export function buildHowToStepsSchema(opts: {
  name: string;
  description?: string;
  path: string;
  steps: (string | { step?: number; title: string; body?: string })[];
  totalTime?: string;
  inLanguage?: string;
}) {
  const site = getActiveSiteUrl();
  const url = `${site}${opts.path.startsWith("/") ? opts.path : `/${opts.path}`}`;
  const step = opts.steps
    .map((raw, i) => {
      const row =
        typeof raw === "string" ? { title: raw, body: undefined, step: i + 1 } : raw;
      const name = String(row.title ?? "").trim();
      if (!name) return null;
      const text = String(row.body ?? row.title ?? "").trim();
      return {
        "@type": "HowToStep",
        position: row.step ?? i + 1,
        name: name.slice(0, 110),
        text,
        url: `${url}#step-${row.step ?? i + 1}`,
      };
    })
    .filter(Boolean);
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "@id": `${url}#howto`,
    name: opts.name.slice(0, 110),
    ...(opts.description ? { description: opts.description.slice(0, 300) } : {}),
    ...(opts.inLanguage ? { inLanguage: opts.inLanguage } : {}),
    totalTime: opts.totalTime ?? "P3D",
    step,
  };
}


/**
 * Speakable markup for voice assistants and AI answer engines.
 * Pass the page path (e.g. "/legal/faq") so each page emits its own
 * canonical @id/url instead of collapsing onto the homepage node.
 */
export function buildSpeakableSchema(path?: string) {
  const site = getActiveSiteUrl();
  const url = path ? `${site}${path.startsWith("/") ? path : `/${path}`}` : `${site}/`;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [
        "#ai-answer-tldr",
        "#ai-answer-facts",
        "h1",
        '[data-speakable="true"]',
      ],
    },
  };
}

export function buildAreasItemListSchema(
  areas: { slug: string; name: string; description?: string }[],
) {
  const { config } = getActiveProvince();
  const site = getActiveSiteUrl();
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `พื้นที่ให้บริการในจังหวัด${config.nameTh}`,
    numberOfItems: areas.length,
    itemListElement: areas.map((a, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: a.name,
      url: `${site}/areas/${a.slug}`,
      ...(a.description ? { description: a.description } : {}),
    })),
  };
}

/**
 * Person schemas for each notarial-services attorney on the team.
 * Strong E-E-A-T signal — Google ties expertise to a named, credentialed
 * professional rather than an anonymous "team".
 */
export function buildLawyerPersonSchemas(
  lawyers: ReadonlyArray<{
    id: string;
    name: string;
    nameEn: string;
    title: string;
    titleTh: string;
    specialties: readonly string[];
  }>,
) {
  const site = getActiveSiteUrl();
  return lawyers.map((l) => ({
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${site}/about#lawyer-${l.id}`,
    name: l.nameEn,
    alternateName: l.name,
    jobTitle: l.title,
    description: `${l.titleTh} — ขึ้นทะเบียนสภาทนายความแห่งประเทศไทย ตามข้อบังคับ พ.ศ. 2551`,
    knowsAbout: l.specialties,
    worksFor: { "@id": `${site}/#organization` },
    affiliation: {
      "@type": "Organization",
      name: "Lawyers Council of Thailand",
      url: "https://www.lawyerscouncil.or.th/",
    },
    hasCredential: {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "license",
      name: "Notarial Services Attorney License",
      recognizedBy: {
        "@type": "Organization",
        name: "Lawyers Council of Thailand under the Royal Patronage",
      },
    },
  }));
}